import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/App";
import { BizouLogo } from "@/components/BizouLogo";
import { useTranslation } from "@/i18n/LanguageContext";

export default function MagicLinkCallback({ setUser }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current || !token) return;
    hasProcessed.current = true;

    const verify = async () => {
      try {
        const response = await fetch(`${API}/auth/magic-link/verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ token }),
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          setError(data.detail || t("toast.magicLinkExpired"));
          return;
        }

        const data = await response.json();
        if (setUser && data.user) setUser(data.user);
        // Respect destination saved by ProtectedRoute (e.g. /invoices)
        let redirectTo = "/dashboard";
        try {
          const stored = sessionStorage.getItem("bizou_redirect_after_login");
          if (stored && stored.startsWith("/") && !stored.startsWith("//")) {
            redirectTo = stored;
            sessionStorage.removeItem("bizou_redirect_after_login");
          }
        } catch (e) { /* ignore */ }
        navigate(redirectTo, { replace: true });
      } catch {
        setError(t("toast.magicLinkExpired"));
      }
    };

    verify();
  }, [token, navigate, setUser, t]);

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="max-w-sm w-full text-center">
          <BizouLogo size="lg" className="justify-center mb-6" />
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <p className="text-[#78716C] mb-6">{error}</p>
          <Button onClick={() => navigate("/")} className="btn-primary" data-testid="back-home-btn">
            {t("common.back")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center">
        <BizouLogo size="lg" className="justify-center mb-6" />
        <Loader2 className="w-8 h-8 text-[#E8175D] mx-auto animate-spin mb-4" />
        <p className="text-[#78716C]">{t("toast.magicLinkVerifying")}</p>
      </div>
    </div>
  );
}
